import { Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export type LeaveTypesSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  isActive?: boolean;
  search?: string;
};

const VALUE_ALL = "__all__";
const STATUS_OPTIONS = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "true", label: "ໃຊ້ງານ" },
  { value: "false", label: "ປິດ" },
];

type LeaveTypesFilterProps = {
  searchParams: LeaveTypesSearchParams;
  fromRoute: "/app/hr/leave/types";
};

export function LeaveTypesFilter({
  searchParams,
  fromRoute,
}: LeaveTypesFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;
  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [statusInput, setStatusInput] = useState(
    search.isActive === true
      ? "true"
      : search.isActive === false
        ? "false"
        : VALUE_ALL,
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setStatusInput(
      search.isActive === true
        ? "true"
        : search.isActive === false
          ? "false"
          : VALUE_ALL,
    );
  }, [search.search, search.isActive]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        isActive:
          statusInput === VALUE_ALL ? undefined : statusInput === "true",
        search: val.trim() || undefined,
      },
    });
  }, 400);

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="leave-types-search"
            aria-label="ຄົ້ນຫາຕາມຊື່ ຫຼື ລະຫັດ"
            placeholder="ຄົ້ນຫາປະເພດການລາຕາມຊື່ ຫຼື ລະຫັດ..."
            className="h-8 pl-8 sm:max-w-2xs"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <SimpleSelect
          options={STATUS_OPTIONS}
          value={statusInput}
          onValueChange={(v) => {
            setStatusInput(v);
            nav({
              search: {
                offset: 0,
                limit: search.limit ?? 20,
                sort: search.sort,
                isActive: v === VALUE_ALL ? undefined : v === "true",
                search: searchInput.trim() || undefined,
              },
            });
          }}
          placeholder="ສະຖານະ"
          className="h-8 w-full sm:w-32"
          aria-label="ກັ່ນຕອງຕາມສະຖານະ"
        />
      </div>
    </div>
  );
}
